import Vue from 'vue';
import Router from 'vue-router';
import ViewUI from 'view-design';
import routes from './routes';
import config from '@/config';
import {
    clearLoginStatus,
    isNotLoginPage,
    canTurnTo,
    checkInIframe,
    localSave,
    localRead,
    localRemove,
    sessionSave,
    sessionRemove,
    isSSO,
    getMenuByRouter,
    hasAccess,
    isShellEmbedded,
} from '@/libs/util';
import { routeNeedsSocket, ensureSocket, releaseSocketIfActive } from '@/libs/socket-manager';
import { NO_LOGOUT_PAGE, NO_SAVE_LOCAL_NAV, REDIRECT_WHITE_LIST, THIRD_PARTY_SSO_K_I } from '@/constants/common';
import { loadLanguageAsync, getLang, initLanguage } from '@/locales';
import { keepBackParamsRouterMap } from '@/config/constant';
import { resolveNavigation, notifyShellNavigate, isShellDrivenNavigation } from '@/libs/embedded-bridge';
import {
    getLocalIsNewUiValue,
    shouldRedirectToNextByLocalIsNewUi,
} from './new-ui-redirect';

const {
    tokenHomeName,
    authHomeName,
    title,
    siteConfig,
} = config;

const getName = (access: any): string => {
    let goName: string = '';
    if (!access) {
        return '';
    }
    routes.map((item: any) => {
        if (item.children && item.children.length && !goName) {
            item.children.map((child: any) => {
                const isAuth = canTurnTo(child.name, access, routes);
                if (child.meta.access && isAuth && !goName) {
                    goName = child.name;
                }
                return child;
            });
        }
        return item;
    });
    return goName;
};

export const whereToGo = (userId: string, access: any, mode: string = 'TOKEN_MODE') => {
    const authName = getName(access) || (mode === 'TOKEN_MODE' ? tokenHomeName : authHomeName);
    // 嵌入模式下不读 lastNav_${userId}:这条 storage 是跨会话/跨 tab 共享的"上次访问页"记忆,
    // iframe 在 embedded 场景下读它会把另一个 tab 写入的页错当成本 tab 的 home 落地点,
    // 进而通过 iframe:route-change 把 shell URL 拉到错误位置(典型 bug:Teambition 69ec67f7、69ef537e)。
    // 嵌入模式下 home 决策权应在 shell;iframe 这一头退到"权限内首个可达页"做确定性兜底即可。
    if (isShellEmbedded()) {
        return {
            name: authName,
            params: {},
        };
    }
    const lastNav = localRead(`lastNav_${userId}`);
    const lastNavParams = JSON.parse(localRead(`lastNavParams_${userId}`) || '{}');
    if (lastNav && canTurnTo(lastNav, access, routes) && !isSSO()) {
        return {
            name: lastNav,
            params: lastNavParams,
        };
    }
    return {
        name: authName,
        params: {},
    };
};

const getMenuList = (menuList: any[], access: string[], isCaiNiao: boolean) => {
    let list: any[] = [];
    // 无二级菜单
    const specialMenus = ['authMode_chat', 'channels', 'authMode_setting_team', 'authMode_setting', 'appstore'];
    if (!isCaiNiao) {
        specialMenus.push('fee');
    }
    menuList.forEach((item: any) => {
        const child = getMenuByRouter(item.children || [], access);
        if (specialMenus.includes(item.name)) {
            list.push(item);
        } else if (item.children && child.length) {
            if (!['authMode_reward', 'helpdesk'].includes(item.name)) {
                list = list.concat(child);
            }
        }
    });
    return list.map((x) => x.name);
};

Vue.use(Router);

// 抑制 vue-router 3 的"预期"导航错误（NavigationDuplicated/NavigationRedirected/NavigationCancelled）
// 这些属于 guard 正常跳转场景（例如迁移判定重定向到 /idle），不是真实错误
// 参考：vue-router 3 官方 issue #2881 的通用解法
const suppressedNavErrors = new Set(['NavigationDuplicated', 'NavigationRedirected', 'NavigationCancelled']);
['push', 'replace'].forEach((method) => {
    const original = (Router.prototype as any)[method];
    (Router.prototype as any)[method] = function patched(location: any, onResolve?: Function, onReject?: Function) {
        if (onResolve || onReject) {
            return original.call(this, location, onResolve, onReject);
        }
        return original.call(this, location).catch((err: any) => {
            if (err && suppressedNavErrors.has(err.name)) {
                // 静默预期的导航流程错误
                return err;
            }
            throw err;
        });
    };
});

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    routes,
});

const LOGIN_PAGE_NAME = 'login';

const resolveAbsoluteHref = (location: any): string => new URL(
    router.resolve(location).href,
    window.location.origin,
).href;

/**
 * 根据当前目标路由拼出登录页跳转位置。
 *
 * 对受保护页面会额外带上当前地址，方便登录完成后按原逻辑回跳。
 *
 * @param to 当前即将进入的目标路由。
 * @returns 传给 `router.resolve` 或 `next` 的登录页 location 对象。
 */
const getLoginLocation = (to: any) => {
    const query = {
        ...(to.query || {}),
    };

    if (to.name !== 'home' && !isNotLoginPage(to.name as string)) {
        query.url = escape(window.location.href);
    }

    return {
        name: LOGIN_PAGE_NAME,
        query,
    };
};

/**
 * 通过浏览器地址栏执行一次硬跳转。
 *
 * 这里不走 `router.push/replace`，用于需要强制刷新页面上下文的场景。
 * 如果解析后的目标地址与当前浏览器地址完全一致，则直接返回 `false`，
 * 避免登录页这类入口在守卫里反复触发同地址刷新。
 *
 * @param location 目标 location，对象格式与 vue-router 保持一致。
 * @returns 实际触发浏览器硬跳转时返回 `true`，命中同地址保护时返回 `false`。
 */
const hardNavigate = (location: any) => {
    const targetHref = resolveAbsoluteHref(location);
    if (targetHref === window.location.href) {
        return false;
    }
    window.location.href = targetHref;
    return true;
};

/**
 * 在需要刷新登录页上下文时优先执行一次硬跳转；如果当前已经处在同一个登录地址，
 * 则回退到普通路由重定向，避免守卫内出现登录页自刷新死循环。
 *
 * @param to 当前即将进入的目标路由。
 * @param next 当前导航守卫的 `next` 回调。
 */
const hardNavigateToLogin = (to: any, next: any) => {
    const loginLocation = getLoginLocation(to);
    if (hardNavigate(loginLocation)) {
        return;
    }

    const { route } = router.resolve(loginLocation);
    if (route.fullPath === to.fullPath) {
        next();
        return;
    }

    next({
        ...loginLocation,
        replace: true,
    });
};

/**
 * 将当前目标路由转换成 `/next` 应用地址并执行硬跳转。
 *
 * 如果目标地址已经是 `/next` 前缀，则直接复用，避免重复拼接。
 *
 * @param to 当前即将进入的目标路由。
 */
const hardNavigateToNext = (to: any) => {
    const { href } = router.resolve(to);
    if (href.indexOf('/next') === 0) {
        window.location.href = href;
        return;
    }
    window.location.href = `/next${href}`;
};

/**
 * 在用户已登录且具备基础上下文后，统一处理旧版路由的权限放行、首页重定向和新版跳转。
 *
 * 这个函数属于第三段前置守卫里的核心业务决策层，负责把“能否访问”和“该跳到哪里”
 * 这两件事集中收口，避免在守卫主体里散落过多分支。
 *
 * @param args 当前导航、用户权限以及路由跳转所需的上下文信息。
 * @returns 始终返回 `true`，实际跳转行为通过 `next` 或硬跳转完成。
 */
const turnTo = ({
    to,
    from,
    access,
    next,
    userId,
    systemMode,
    isNewUi,
}: {
    to: any,
    from: any,
    access: any,
    next: any,
    userId: string,
    systemMode: string,
    isNewUi?: '0' | '1' | '2' | '3' | '4',
}) => {
    if (canTurnTo(to.name, access, routes)) {
        if (isSSO()) {
            next();
            return true;
        }

        // 有权限，可访问
        const lastNav = whereToGo(userId, access, systemMode);
        if (to.name === 'home') {
            console.log('router-to-home');
            next({
                replace: true,
                name: lastNav.name,
                params: lastNav.params,
            });
        } else if (to.meta.notPageView) {
            // 如果进入的是第一层路由，自动跳转到第一个有权限的子路由
            let routerInfo: any = routes;
            to.matched.forEach((matchedItem: any) => {
                const match = routerInfo.find((item: any) => matchedItem.name === item.name);
                if (match && match.children && Array.isArray(match.children)) {
                    routerInfo = match.children || [];
                }
            });

            const nextPage = routerInfo.find((item: any) => hasAccess(access, item));
            if (nextPage) {
                next({
                    replace: true,
                    name: nextPage.name,
                    params: to.params,
                });
            } else {
                next({ replace: true, name: 'error401' });
            }
        } else {
            // eslint-disable-next-line no-prototype-builtins
            const isTargetRoute = keepBackParamsRouterMap.hasOwnProperty(to.name);
            const isFromRoute = keepBackParamsRouterMap[to.name]?.includes(from.name) || false;
            if (from.name && isTargetRoute && isFromRoute && from.params?.ss_source_back_data) {
                to.params.ss_target_back_data = from.params?.ss_source_back_data;
            }

            // 非白牌，跳转新版(这里只能拦截从其他界面过来的时候)
            // 兜底拦截在 src/views/main/authMode/Chat/index.vue
            // 嵌入模式下 shell 已接管新/旧版切换，iframe 内部不应主动跳 /next/...（否则 iframe 窗口会加载 shell 造成 shell 嵌套 shell）
            if ((isNewUi !== '0' && isNewUi !== '1') && !siteConfig.brand && !REDIRECT_WHITE_LIST.includes(to.name) && !isShellEmbedded()) {
                // 跳转新版
                const { href } = router.resolve(to);
                // 防止在旧版本地开发的时候死循环跳转了
                if (href.indexOf('/next') === 0) {
                    next();
                } else {
                    window.location.href = `/next${href}`;
                }
            } else {
                next();
            }
        }
    } else {
        const lastLocalNav = localRead(`lastNav_${userId}`);
        const vm: any = router.app;
        const menuList = vm.$options.store.getters['main/menuList'];
        const isCaiNiao = vm.$options.store.getters['main/isCaiNiao'];
        const sideMenuList = getMenuList(menuList, access, isCaiNiao);

        // 首页 或者是401页面的返回上一页，判断是否有权限
        if ((['home', 'index_home'].includes(to.name as string) && sideMenuList && sideMenuList.length) || !lastLocalNav) {
            // 跳转到第一个有权限的页面
            const name = sideMenuList[0];
            next({
                replace: true,
                name,
                params: {},
            });
            return true;
        }

        // 无权限，重定向到401页面
        next({ replace: true, name: 'error401' });
    }
    return true;
};

const getGrayReleaseUrl = (projectIdOrUserId: string, data: any): string => {
    if (!projectIdOrUserId || !data) {
        return '';
    }
    let baseUrlRes = '';
    for (const baseUrl in data) {
        if (baseUrl && data[baseUrl]) {
            const projectIds = data[baseUrl].company_ids;
            for (const projectId of projectIds) {
                if (`${projectIdOrUserId}` === `${projectId}`) {
                    baseUrlRes = baseUrl;
                    break;
                }
            }
            const userIds = data[baseUrl].user_ids;
            for (const userId of userIds) {
                if (`${projectIdOrUserId}` === `${userId}`) {
                    baseUrlRes = baseUrl;
                    break;
                }
            }
        }
    }
    if (baseUrlRes && baseUrlRes.charAt(baseUrlRes.length - 1) !== '/') {
        baseUrlRes = `${baseUrlRes}/`;
    }
    return baseUrlRes;
};

// iframe 嵌入模式下，若目标路径已迁移到 salesmartly-fe：
// - 通知 shell 切换到 native / canonical 路径
// - 自身 next('/idle') 释放资源
router.beforeEach((to, from, next) => {
    if (!isShellEmbedded()) {
        next();
        return;
    }
    // 避免 /idle 自身、同路径重入造成死循环
    if (to.path === '/idle' || to.path === from.path) {
        next();
        return;
    }
    // shell 主动发起的 shell:navigate 已由 shell 决策完成（包括 /chat 这类 iframeForceShow 场景），
    // iframe 必须无条件执行，不能被迁移判定拦截
    if (isShellDrivenNavigation()) {
        next();
        return;
    }

    const decision = resolveNavigation(to.path);
    switch (decision.kind) {
        case 'rewrite':
            notifyShellNavigate(decision.newPath);
            next({ path: '/idle', replace: true });
            return;
        case 'native':
            notifyShellNavigate(to.fullPath);
            next({ path: '/idle', replace: true });
            return;
        case 'legacy':
        default:
            next();
    }
});

router.beforeEach((to, from, next) => {
    const toPath = to?.fullPath || from?.fullPath;
    window.onunhandledrejection = (event: any) => {
        if (event && event.reason && event.reason.message && event.reason.message.includes('Failed to fetch dynamically imported module')) {
            const expired = localRead('UNHANDLE_REJECTION');
            if (!expired || Date.now() - +expired > 60 * 1000) {
                window.onunhandledrejection = null;
                localSave('UNHANDLE_REJECTION', Date.now().toString());
                window.location.href = toPath;
            }
        }
    };

    // 记录营销参数
    let hasRemktSource = false;
    if (to.query.remkt_source) {
        sessionSave('REMKT_SOURCE', to.query.remkt_source as string);
        sessionSave('REMKT_SOURCE_URL', window.location.href);
        hasRemktSource = true;
        delete to.query.remkt_source;
    }

    const language =
        ['registration', 'login'].includes(to.name || '') && to.query.lang ?
            initLanguage(to.query.lang as string) :
            getLang();
    loadLanguageAsync(language).finally(() => {
        if (hasRemktSource) {
            next({
                path: to.path,
                query: to.query,
                params: to.params || {},
                hash: to.hash,
                replace: true,
            });
        } else {
            next();
        }
    });

    ViewUI.LoadingBar.finish();
});

router.beforeEach((to, from, next) => {
    if (to.name === 'authSSO') {
        localRemove(config.tokenKey);
        sessionRemove(THIRD_PARTY_SSO_K_I);
        next();
        return;
    }

    // ViewUI.LoadingBar.start();

    // 白牌隐藏路由重定向
    try {
        if (siteConfig.brand && siteConfig?.hideMenu && siteConfig.hideMenu.includes(to.name)) {
            next({
                name: 'authMode_chat',
            });
            return;
        }
    } catch (e) {
        console.error('siteConfig error', e);
    }

    const vm: any = router.app;
    const token = localRead(config.tokenKey);
    const query = to.query || {};
    const isPublicRoute = isNotLoginPage(to.name as string);
    // 不能执行"用户登出"的页面
    if (NO_LOGOUT_PAGE.includes(to.name as string) || to.params.token || (window as any).SS_ACTIVITY) {
        next();
        return;
    }

    // 针对wordpress跳转做特殊处理
    if (to.name === 'login' && query.source === 'wordpress') {
        if (query.type && ['authorized_shop', 'unauthorized_shop'].includes(query.type as string)) {
            vm.$options.store.dispatch('user/passport/passiveLogout');
            next();
        } else {
            // 跳转中转页判断店铺绑定状态
            next({
                name: 'authShop',
                query,
            });
        }
        return;
    }

    const localIsNewUi = getLocalIsNewUiValue(localRead('ss_new_ui'));

    if (!token && !isPublicRoute && !siteConfig.brand) {
        hardNavigateToLogin(to, next);
        return;
    }

    if (shouldRedirectToNextByLocalIsNewUi({
        localIsNewUi,
        hasToken: Boolean(token),
        isInIframe: checkInIframe(),
        isPublicRoute,
        routeName: to.name as string | undefined,
    })) {
        hardNavigateToNext(to);
        return;
    }

    if (!token && isPublicRoute) {
        if (to.name === 'payresult') {
            next();
            return;
        }
        vm.$options.store.dispatch('user/passport/passiveLogout');
        next();
        return;
    }
    const { access, userId } = vm.$options.store.state.user;
    let { systemMode, isNewUi } = vm.$options.store.state.user;
    if (userId) {
        if (isPublicRoute) {
            const lastNav = whereToGo(userId, access, systemMode);

            if (isSSO()) {
                next();
                return;
            }

            next({
                name: lastNav.name,
                params: lastNav.params,
            });
            return;
        }
        turnTo({
            to,
            from,
            access,
            next,
            userId,
            systemMode,
            isNewUi,
        });
    } else if (to.name === 'noProject') {
        // 如果已经在 noProject 页面，不再请求 getUserInfo
        next();
    } else {
        vm.$options.store.dispatch('user/profile/getUserInfo').then((res: any) => {
            // 获取信息之后再读取一次
            systemMode = vm.$options.store.state.user.systemMode;
            isNewUi = vm.$options.store.state.user.isNewUi;

            if (isPublicRoute) {
                const lastNav = whereToGo(res.data.id, res.data.auth_list, systemMode);
                next({
                    name: lastNav.name,
                    params: lastNav.params,
                });
                return;
            }
            // 如果projectId 是 0 则表示没有项目
            if (Number(vm.$options.store.state.user.projectId) === 0) {
                next({
                    name: 'noProject',
                });
                return;
            }

            turnTo({
                to,
                from,
                access: res.data.auth_list,
                next,
                userId: res.data.id,
                systemMode,
                isNewUi,
            });
        }).catch(() => {
            vm.$options.store.dispatch('user/passport/passiveLogout');
            clearLoginStatus();
            if (isPublicRoute) {
                next();
            } else if (!siteConfig.brand) {
                hardNavigateToLogin(to, next);
            } else {
                next(getLoginLocation(to));
            }
        });
    }
});

router.afterEach((to, from) => {
    window.onunhandledrejection = null;

    const vm: any = router.app;
    const { name, params } = to;
    if ((window as any).SS_ACTIVITY) {
        document.title = 'SaleSmartly';
    } else {
        const routeName = vm.$t(`nav.${to.name}`);
        let titleText = `${title}`;
        const envTxt = process.env.VUE_APP_CURRENTMODE || process.env.NODE_ENV;
        titleText += ` | ${routeName}`;
        if (envTxt && envTxt !== 'pro' && envTxt !== 'production') {
            titleText += `【${envTxt}】`;
        }
        document.title = titleText;
    }
    ViewUI.LoadingBar.finish();
    window.scrollTo(0, 0);
    const { userId } = vm.$options.store.state.user;
    if (name && !isNotLoginPage(name) && !NO_SAVE_LOCAL_NAV.includes(name)) {
        if (!isSSO()) {
            localSave(`lastNav_${userId}`, name);
            localSave(`lastNavParams_${userId}`, JSON.stringify(params));
        }
    }
});

router.afterEach((to, from) => {
    if (!isShellEmbedded()) return;
    const toNeedsSocket = routeNeedsSocket(to.path);
    const fromNeedsSocket = from.path ? routeNeedsSocket(from.path) : false;
    if (toNeedsSocket && !fromNeedsSocket) {
        ensureSocket();
    } else if (!toNeedsSocket && fromNeedsSocket) {
        releaseSocketIfActive();
    }
});

router.afterEach((to) => {
    if (!isShellEmbedded()) return;
    // /idle 是 iframe 自己的资源释放页，不应覆盖 shell URL
    if (to.path === '/idle') return;
    try {
        window.parent.postMessage(
            {
                v: 1,
                type: 'iframe:route-change',
                source: 'iframe',
                payload: {
                    path: to.fullPath,
                    name: to.name,
                },
            },
            window.location.origin,
        );
    } catch (e) {
        console.error('[router] failed to post iframe:route-change', e);
    }
});

export default router;
